/**
 * 注册页面
 */
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { authApi } from '../services/authApi';

const Register: React.FC = () => {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const setAuth = useAuthStore((state) => state.setAuth);
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    full_name: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/home', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('两次输入的密码不一致');
      return;
    }

    setLoading(true);

    try {
      await authApi.register({
        username: formData.username,
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name || undefined,
      });
      const loginResponse = await authApi.login({
        username: formData.username,
        password: formData.password,
      });
      setAuth(loginResponse.data.access_token, loginResponse.data.user);
    } catch (err: any) {
      let errorMessage = '注册失败，请检查输入信息';
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          errorMessage = detail.map((e: any) => e.msg || JSON.stringify(e)).join('; ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-emerald-50 px-4 py-12">
      <div className="w-full max-w-[400px] bg-white rounded-2xl border border-border shadow-modal p-8">
        {/* Logo + 标题 */}
        <div className="text-center mb-8">
          <img
            src={`${process.env.PUBLIC_URL}/logo.png`}
            alt="小灵标"
            className="mx-auto mb-4 h-20 w-20 rounded-xl object-cover"
          />
          <h1 className="text-2xl font-extrabold text-foreground tracking-tight text-center">小灵标</h1>
          <p className="mt-1 text-sm text-muted-foreground">创建账户，开始智能写标书</p>
        </div>
          <h2 className="text-lg font-bold text-foreground mb-6">注册</h2>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="username" className="block text-xs font-medium text-muted-foreground mb-1.5">用户名</label>
              <input
                id="username" type="text" required
                value={formData.username}
                onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                className="input"
                placeholder="请输入用户名"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-xs font-medium text-muted-foreground mb-1.5">邮箱</label>
              <input
                id="email" type="email" required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input"
                placeholder="请输入邮箱"
              />
            </div>

            <div>
              <label htmlFor="full_name" className="block text-xs font-medium text-muted-foreground mb-1.5">姓名（可选）</label>
              <input
                id="full_name" type="text"
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className="input"
                placeholder="请输入姓名"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-xs font-medium text-muted-foreground mb-1.5">密码</label>
              <input
                id="password" type="password" required
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="input"
                placeholder="请输入密码"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-xs font-medium text-muted-foreground mb-1.5">确认密码</label>
              <input
                id="confirmPassword" type="password" required
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                className="input"
                placeholder="请再次输入密码"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
            >
              {loading ? '注册中...' : '注册'}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            已有账户？{' '}
            <Link to="/login" className="font-semibold text-primary hover:text-primary/80">
              立即登录
            </Link>
          </p>
        </div>
    </div>
  );
};

export default Register;
